import React, { useState } from 'react';
import styled from 'styled-components';
import RegistrationView  from './RegistrationView';
import { ORDER_TYPE } from '../common/constants/OrderType';
import { downloadBundledFile } from '../common/utils/DownloadUtils';
import { DownloadIcon, IndividualIcon, VerifiedIcon } from '@parchment/icons';
import { Anchor, breakpoints, Button, Divider, FormContainer, Heading4,
    Paragraph } from '@parchment/atoms';

const noop = () => {};
const handleConsentFileDownload = () => {
    downloadBundledFile(require('file-loader?name=[path][name].[ext]!../media/credential_request_authorization_form.pdf'), 'credential_request_authorization_form.pdf');
};

export default function ThirdPartyConfirmationPage ({
    email = '',
    member = {},
    resetEmail = noop,
    downloadConsentFile = handleConsentFileDownload
}) {
    const [ agreeToContinue, setAgreeToContinue ] = useState(false);

    const renderTPORegistrationForm = () => {
        setAgreeToContinue(true);
    };

    if (agreeToContinue) {
        return (
            <RegistrationView email={email}
                              member={member}
                              resetEmail={() => resetEmail(ORDER_TYPE.THIRD_PARTY)}
                              orderType={ORDER_TYPE.THIRD_PARTY} />
        );
    } else {
        return (
            <FormContainer context='recessed' className='third-party-confirmation'>
                <ConfirmationHeader className='confirmation-header'>
                    Is a Third Party Account right for you?
                </ConfirmationHeader>
                <StyledParagraph className='confirmation-text'>
                    Third Party Accounts are for those who need to order
                    transcripts or academic records on <b>behalf of
                    someone else.</b> Typically, these are verification
                    companies ordering records to verify an individual&apos;s
                    credentials or enrollment status.
                </StyledParagraph>

                <RequirementsHeader className='requirements-header'>
                    Additional requirements for ordering on behalf of someone else.
                </RequirementsHeader>
                <RequirementsSection className='requirements-section'>
                    <RequirementsSubSection className='individual-information'>
                        <div><StyledIndividualIcon className='individual-icon'/></div>
                        <SectionHeader className='individual-information-header'>
                            Individual (Learner) Information
                        </SectionHeader>
                        <StyledParagraph className='individual-information-text'>
                            You will be asked for information about the learner
                            such as name, other names used while attending,
                            DOB, and perhaps more depending on the record
                            holder&apos;s requirements.
                            Please have this info handy.
                        </StyledParagraph>
                    </RequirementsSubSection>
                    <Divider orientation='vertical' />
                    <RequirementsSubSection className='learner-consent'>
                        <div><StyledVerifiedIcon className='verified-icon'/></div>
                        <SectionHeader className='learner-consent-header'>
                            Learner Consent
                        </SectionHeader>
                        <ConsentDownloadLink>
                            <Anchor className='consent-download-link'
                                    onClick={() => downloadConsentFile()}>
                                <StyledDownloadIcon className='download-icon'/>
                                Download Learner Consent form
                            </Anchor>
                        </ConsentDownloadLink>
                        <StyledParagraph className='learner-consent-text'>
                            You may use your own FERPA compliant consent form if
                            already in your possession.
                            (Subject to approval by the record holder)
                        </StyledParagraph>
                    </RequirementsSubSection>
                </RequirementsSection>

                <ButtonWrapper>
                    <ChangeRegistrationButton name='change-registration-type'
                                              className='modify-registration'
                                              onClick={() => resetEmail()}
                                              ghost >
                        I Am Actually Trying To Order My Own Credential
                    </ChangeRegistrationButton>
                    <Button name='continue-tpo-registration'
                            className='continue-tpo-ordering'
                            onClick={() => renderTPORegistrationForm()}>
                        Continue
                    </Button>
                </ButtonWrapper>
            </FormContainer>
        );
    }
};

const StyledIndividualIcon = styled(IndividualIcon)`
    font-size: 32px;
`;
StyledIndividualIcon.displayName = 'StyledIndividualIcon';

const StyledVerifiedIcon = styled(VerifiedIcon)`
    font-size: 32px;
`;
StyledVerifiedIcon.displayName = 'StyledVerifiedIcon';

const StyledDownloadIcon = styled(DownloadIcon)`
    font-size: 16px;
    margin-right: 15px;
`;
StyledDownloadIcon.displayName = 'StyledDownloadIcon';

const BaseHeader = styled(Heading4)`
    font-size: 16px;
    font-weight: 600;
`;
BaseHeader.displayName = 'BaseHeader';

const ConfirmationHeader = styled(BaseHeader)`
    padding-bottom: 20px;
`;
ConfirmationHeader.displayName = 'ConfirmationHeader';

const StyledParagraph = styled(Paragraph)`
    font-size: 14px;
`;
StyledParagraph.displayName = 'StyledParagraph';

const RequirementsHeader = styled(BaseHeader)`
    padding: 20px 0;
    text-align: center;
`;
RequirementsHeader.displayName = 'RequirementsHeader';

const RequirementsSection = styled.div`
    display: flex;
    flex-direction: row;
`;
RequirementsSection.displayName = 'RequirementsSection';

const RequirementsSubSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 0 10px;
    flex: 1;
`;
RequirementsSubSection.displayName = 'RequirementsSubSection';

const SectionHeader = styled(BaseHeader)`
    padding-bottom: 10px;
`;
SectionHeader.displayName = 'SectionHeader';

const ConsentDownloadLink = styled.div`
    margin-bottom: 10px;
`;
ConsentDownloadLink.displayName = 'ConsentDownloadLink';

const ButtonWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    margin-top: 10px;
    column-gap: 50px;
    row-gap: 10px;
`;

const ChangeRegistrationButton = styled(Button)`
    white-space: break-spaces;
    line-height: 16px;

    @media (min-width: ${breakpoints.mediumMin}) {
        line-height: 22px;
    }
`;
ChangeRegistrationButton.displayName = 'ChangeRegistrationButton';
